import React, { useState } from 'react';
import {array, bool, func, object, string} from 'prop-types';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { get, find, isEmpty } from 'lodash';

import {connect} from 'react-redux';

import { v4 as uuidv4 } from 'uuid';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import EsmsDialog from '../../components/EsmsDialog';
import AutoComplete from '../../components/FormFields/Autocomplete';

import * as ArchitectActions from '../../actions/architectActions';
import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as BasAgentActions from '../../actions/basAgentActions';
import * as WizardActions from '../../actions/wizardActions';

import classes from './EditEntityModal.module.css';
import { displayUnitData } from '../../data/displayUnitData';

const ValidationSchema = Yup.object().shape({
    name: Yup.string()
        .max(100)
        .required(),
    deviceObject: Yup.string()
        .required(),
    dataType: Yup.string()
        .required(),
    sensorType: Yup.object()
        .required()
        .nullable(),
    rawUnit: Yup.string()
        .when('dataType', {
            is:        'float',
            then:      Yup.string().required(),
            otherwise: Yup.string()
        })
        .nullable(),
    displayUnit: Yup.string()
        .when('dataType', {
            is:        'float',
            then:      Yup.string().required(),
            otherwise: Yup.string()
        })
        .nullable(),
    displayDataType: Yup.string(),
    conversion:      Yup.string()
        .nullable()
});

const DEFAULT_FORM_VALUES = {
    name:            '',
    deviceObject:    '',
    dataType:        '',
    sensorType:      '',
    rawUnit:         '',
    displayUnit:     '',
    displayDataType: '',
    conversion:      ''
};

const InitialValues = {...DEFAULT_FORM_VALUES};

const ConfigureSensorModal = ({
    close,
    conversions,
    object,
    open,
    sensorTypes,
    targetDevices,
    title,
    toggleLoading,
    units,
    ...props
}) => {
    const [displayUnits, setDisplayUnits] = useState([]);
    const [filteredConversions, setFilteredConversions] = useState([]);
    const [unitTypeDisabled, setUnitTypeDisabled] = useState(false);

    const formik = useFormik({
        initialValues:      InitialValues,
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                addSensor,
                setTargetProfileObject
            } = props;

            setSubmitting(true);

            const feId = (object.sensorFrontEndId && !isEmpty(object.sensorFrontEndId)) ? object.sensorFrontEndId : uuidv4();

            const sensor = {
                name:               values.name,
                sensorTypeId:       values.sensorType.id,
                rawUnitId:          values.rawUnit.id,
                presentationUnitId: values.displayUnit.id,
                equipmentId:        '',
                systemId:           '',
                deviceObjectId:     '',
                conversionId:       values.conversion === 'No Conversion' ? '' : values.conversion,
                feId,
                dataType:           values.dataType,
                displayDataType:    values.displayDataType
            };

            object.sensorFrontEndId = sensor.feId;
            object.isConfigured = true;
            object.sensorTypeId = values.sensorType ? values.sensorType.id : '';
            object.sensorTypeName = values.sensorType ? values.sensorType.name : '';
            object.displayUnitId = values.displayUnit ? values.displayUnit.id : '';
            object.displayUnitName = values.displayUnit ? values.displayUnit.name : '';

            addSensor(sensor);
            setTargetProfileObject(object);

            setSubmitting(false);

            handleClose();
        }
    });

    const getUnit = unitId => {
        let targetUnit;

        units.forEach(unit => {
            unit.items.forEach(item => {
                if (item.id === unitId) {
                    targetUnit = {...item};
                }
            });
        });

        return targetUnit;
    };

    const prepareDropdowns = async () => {
        const filteredConversions = conversions.filter(conversion => conversion.rawDataType === object.dataType);

        setFilteredConversions(filteredConversions);
    };

    const getSensorType = id => {
        if (isEmpty(id)) return {};

        return sensorTypes[0].items.find(type => type.id === id);
    };

    const handleSensorTypeChange = event => {
        const {value} = event.target;

        if (formik.values.name) {
            formik.handleChange(event);

            return;
        }

        if (value && value.name) {
            formik.setFieldValue('name', value.name, false);
        }

        formik.handleChange(event);
    };

    const handleRawUnitChange = event => {
        const { value } = event.target;
        let displayUnits;

        units.forEach(group => {
            if (value && group.name === value.unitType) {
                displayUnits = [group];
                return;
            }
        });

        setDisplayUnits(displayUnits);

        if (value) {
            formik.setFieldValue('displayUnit', value);
        }

        formik.handleChange(event);
    };

    const handleConversionChange = event => {
        const { value } = event.target;
        let targetConversion;

        if (value === 'No Conversion' || !value.length) {
            targetConversion = get(object, 'dataType', '');
        } else {
            const conversion = find(conversions, item => item.id === value);

            targetConversion = get(conversion, 'displayDataType', '');
        }

        formik.setFieldValue('displayDataType', targetConversion, false);

        formik.handleChange(event);
    };

    const handleReset = () => {
        const { targetSensors } = props;

        prepareDropdowns();

        const targetSensor = targetSensors.find(item => item.feId === object.sensorFrontEndId);

        if (InitialValues.name.length) {
            Object.assign(InitialValues, DEFAULT_FORM_VALUES);
        }

        InitialValues.deviceObject = get(object, 'name', '');
        InitialValues.dataType = get(object, 'dataType', '');
        InitialValues.displayDataType = get(object, 'dataType', '');
        InitialValues.name = get(object, 'name', '');

        // Default Sensor Type
        let sensorTypeId = get(object, 'sensorTypeId', '');
        if (sensorTypeId) {
            let type = sensorTypes[0].items.filter(item => item.name === sensorTypeId);
            InitialValues.sensorType = type && !isEmpty(type[0]) ? type[0] : '';
        }

        // Default Display Unit
        if (!isEmpty(units)) {
            // kilohertz (kHz) - display unit for testing
            let rawUnit = displayUnitData.filter(unit => unit.id === sensorTypeId);
            if (!isEmpty(rawUnit[0])) {
                let unit = {
                    id:        rawUnit[0].unitId,
                    name:      rawUnit[0].value,
                    precision: 2
                };
                InitialValues.rawUnit = unit;
                InitialValues.displayUnit = unit;
            }
        }

        if (object.dataType !== 'float') {
            setUnitTypeDisabled(true);
        } else {
            setUnitTypeDisabled(false);
        }

        if (targetSensor) {
            InitialValues.name = get(targetSensor, 'name', '');
            InitialValues.sensorType = getSensorType(get(targetSensor, 'sensorTypeId', ''));
            InitialValues.displayDataType = get(targetSensor, 'displayDataType', '');

            const rawUnitId = get(targetSensor, 'rawUnitId', '');
            const displayUnitId = get(targetSensor, 'presentationUnitId', rawUnitId);
            const conversionId = get(targetSensor, 'conversionId', '');

            if (rawUnitId && rawUnitId.length) {
                InitialValues.rawUnit = getUnit(rawUnitId);
            }

            if (displayUnitId && displayUnitId.length) {
                InitialValues.displayUnit = getUnit(displayUnitId);
            }

            if (conversionId && conversionId.length) {
                InitialValues.conversion = conversionId;
            } else {
                InitialValues.conversion = '';
            }
        }

        formik.handleReset(InitialValues);
    };

    const handleClose = () => {
        close();
    };

    return (
        <EsmsDialog
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={handleReset}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting}
            confirmText="Save"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/sensorTreeIcon.svg" alt="Sensor Icon" />
                        <span>Configure Sensor</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Select a Sensor Type.
                        </p>
                        <p>
                            Enter a Sensor name.
                        </p>
                        <p>
                            If the Data Type is Float, you must specify a Unit. If the Data Type is Boolean, String or Integer, Units are not required.
                        </p>
                        <p>
                            Select a Conversion.
                        </p>
                        <p>
                            Note: Conversions are not required. Please see the help docs for more information on Conversions.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <AutoComplete
                            grouped
                            label="Sensor Type"
                            name="sensorType"
                            onBlur={formik.handleBlur}
                            onChange={handleSensorTypeChange}
                            options={sensorTypes}
                            placeholder="Sensor Type"
                            value={formik.values.sensorType}
                            error={Boolean(formik.errors.sensorType) && Boolean(formik.touched.sensorType)}
                            helperText={Boolean(formik.touched.sensorType) && formik.errors.sensorType}
                        />
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Sensor Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                disabled
                                id="deviceObject"
                                name="deviceObject"
                                type="text"
                                value={formik.values.deviceObject}
                                label="Device Object"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.deviceObject) && Boolean(formik.touched.deviceObject)}
                                helperText={Boolean(formik.touched.deviceObject) && formik.errors.deviceObject}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                disabled
                                id="dataType"
                                name="dataType"
                                type="text"
                                value={formik.values.dataType}
                                label="Raw Data Type"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.dataType) && Boolean(formik.touched.dataType)}
                                helperText={Boolean(formik.touched.dataType) && formik.errors.dataType}
                            />
                        </FormControl>
                        <AutoComplete
                            grouped
                            disabled={unitTypeDisabled}
                            label="Raw Unit"
                            name="rawUnit"
                            onBlur={formik.handleBlur}
                            onChange={handleRawUnitChange}
                            options={units}
                            placeholder="Raw Unit"
                            value={formik.values.rawUnit}
                            error={Boolean(formik.errors.rawUnit) && Boolean(formik.touched.rawUnit)}
                            helperText={Boolean(formik.touched.rawUnit) && formik.errors.rawUnit}
                        />
                        <AutoComplete
                            grouped
                            disabled={unitTypeDisabled}
                            label="Display Unit"
                            name="displayUnit"
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            options={displayUnits}
                            placeholder="Display Unit"
                            value={formik.values.displayUnit}
                            error={Boolean(formik.errors.displayUnit) && Boolean(formik.touched.displayUnit)}
                            helperText={Boolean(formik.touched.displayUnit) && formik.errors.displayUnit}
                        />
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                disabled
                                id="displayDataType"
                                name="displayDataType"
                                type="text"
                                value={formik.values.displayDataType}
                                label="Display Data Type"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.displayDataType) && Boolean(formik.touched.displayDataType)}
                                helperText={Boolean(formik.touched.displayDataType) && formik.errors.displayDataType}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="conversion"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.conversion) && Boolean(formik.touched.conversion)}
                            >
                                Conversion
                            </InputLabel>
                            <Select
                                name="conversion"
                                id="conversion"
                                value={formik.values.conversion}
                                onChange={handleConversionChange}
                                classes={{
                                    root: classes.select
                                }}
                                inputProps={{
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.conversion) && Boolean(formik.touched.conversion)}
                            >
                                <MenuItem
                                    key={'No Conversion'}
                                    value={'No Conversion'}
                                >
                                    No Conversion
                                </MenuItem>
                                {
                                    filteredConversions.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.conversion &&
                             formik.errors.conversion && <FormHelperText className={classes.error}>{formik.errors.conversion}</FormHelperText>}
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

ConfigureSensorModal.defaultProps = {
    close:         () => {},
    conversions:   [],
    object:        {},
    open:          false,
    sensorTypes:   [],
    targetDevices: [],
    targetSensors: [],
    title:         '',
    units:         []
};

ConfigureSensorModal.propTypes = {
    close:         func,
    conversions:   array,
    object:        object,
    open:          bool,
    sensorTypes:   array,
    targetDevices: array,
    targetSensors: array,
    title:         string,
    units:         array
};

const mapStateToProps = state => {
    return {
        companyInfo:   state.companyInfo,
        conversions:   state.architect.conversions,
        sensorTypes:   state.sensorTypes.data,
        targetDevices: state.wizard.targetDevices,
        targetSensors: state.wizard.targetSensors,
        units:         state.architect.rawUnits
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...BasAgentActions,
    ...WizardActions
};

export default connect(mapStateToProps, mapDispatchToProps)(ConfigureSensorModal);
